import { createSlice } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

const STORAGE_KEY = "squra:consult-form:v1";
const emptyValues = {
  cfullname: "",
  cjobPosition: "",
  ccompanyName: "",
  ctypeIndustrie: "",
  specificTypeIndustries: "",
  cemail: "",
  ccontactNumber: "",
  typeSecurityNeeds: "",
  specificNotes: "",
};

function loadDraft() {
  if (typeof window === "undefined") return emptyValues;
  try {
    const saved = JSON.parse(
      window.localStorage.getItem(STORAGE_KEY) || "null",
    );
    return saved && typeof saved === "object"
      ? { ...emptyValues, ...saved }
      : emptyValues;
  } catch {
    return emptyValues;
  }
}

const consultFormSlice = createSlice({
  name: "consultForm",
  initialState: {
    values: loadDraft(),
    errors: {},
    status: "idle",
    message: "",
  },
  reducers: {
    updateField(state, action) {
      state.values[action.payload.name] = action.payload.value;
      delete state.errors[action.payload.name];
      state.message = "";
    },
    setErrors(state, action) {
      state.errors = action.payload;
    },
    setStatus(state, action) {
      state.status = action.payload.status;
      state.message = action.payload.message || "";
    },
    resetForm(state) {
      state.values = { ...emptyValues };
      state.errors = {};
      state.status = "idle";
      state.message = "";
    },
  },
});

export const consultFormReducer = consultFormSlice.reducer;
const actions = consultFormSlice.actions;

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <span id={id} className="sq-consult-form__error" role="alert">
      {message}
    </span>
  );
}

function ConsultForm({ config }) {
  const dispatch = useDispatch();
  const { values, errors, status, message } = useSelector(
    (state) => state.consultForm,
  );
  const [successOpen, setSuccessOpen] = useState(false);
  const labels = config.labels || {};
  const isOther = values.ctypeIndustrie === "OTHERS";
  const busy = status === "submitting";

  const initialValues = useMemo(
    () => ({
      ctypeIndustrie: config.industries?.[0]?.value || "",
      typeSecurityNeeds: config.solutions?.[0]?.value || "",
    }),
    [config],
  );

  useEffect(() => {
    if (!values.ctypeIndustrie && initialValues.ctypeIndustrie) {
      dispatch(
        actions.updateField({
          name: "ctypeIndustrie",
          value: initialValues.ctypeIndustrie,
        }),
      );
    }
    if (!values.typeSecurityNeeds && initialValues.typeSecurityNeeds) {
      dispatch(
        actions.updateField({
          name: "typeSecurityNeeds",
          value: initialValues.typeSecurityNeeds,
        }),
      );
    }
  }, [
    dispatch,
    initialValues,
    values.ctypeIndustrie,
    values.typeSecurityNeeds,
  ]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
      } catch {
        /* restricted storage */
      }
    }, 180);
    return () => window.clearTimeout(timer);
  }, [values]);

  const update = (event) =>
    dispatch(
      actions.updateField({
        name: event.target.name,
        value: event.target.value,
      }),
    );

  const validate = () => {
    const next = {};
    const required = [
      "cfullname",
      "cjobPosition",
      "ccompanyName",
      "ctypeIndustrie",
      "cemail",
      "ccontactNumber",
      "typeSecurityNeeds",
    ];
    required.forEach((name) => {
      if (!String(values[name] || "").trim())
        next[name] = labels.required || "This field is required.";
    });
    if (values.cemail) {
      const [localPart, domain, ...extraParts] = values.cemail.split("@");
      const domainParts = domain?.split(".") || [];
      const emailIsValid =
        Boolean(localPart) &&
        extraParts.length === 0 &&
        domainParts.length > 1 &&
        domainParts.every(Boolean);
      if (!emailIsValid) {
        next.cemail = labels.invalidEmail || "Enter a valid email address.";
      }
    }
    if (
      values.ccontactNumber &&
      !/^[+\d][\d\s()-]{6,24}$/.test(values.ccontactNumber)
    ) {
      next.ccontactNumber =
        labels.invalidPhone || "Enter a valid phone number.";
    }
    const maxLengths = {
      cfullname: 100,
      cjobPosition: 100,
      ccompanyName: 100,
      ctypeIndustrie: 100,
      specificTypeIndustries: 90,
      cemail: 100,
      ccontactNumber: 30,
    };
    Object.entries(maxLengths).forEach(([name, max]) => {
      if (String(values[name] || "").length > max)
        next[name] = (labels.tooLong || "Maximum {max} characters.").replace(
          "{max}",
          String(max),
        );
    });
    if (String(values.specificNotes || "").length > 2000) {
      next.specificNotes =
        labels.invalidNotes || "Notes must be 2000 characters or fewer.";
    }
    if (isOther && !values.specificTypeIndustries.trim()) {
      next.specificTypeIndustries =
        labels.required || "This field is required.";
    }
    dispatch(actions.setErrors(next));
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const consentEl = formEl.querySelector('input[name="consent"]');
    const hpEl = formEl.querySelector('input[name="website"]');
    // honeypot terisi = bot: diam-diam abaikan
    if (hpEl && hpEl.value) return;
    if (!consentEl || !consentEl.checked) {
      dispatch(
        actions.setStatus({
          status: "error",
          message:
            labels.consentRequired ||
            "Persetujuan penyimpanan data pribadi wajib disetujui.",
        }),
      );
      return;
    }
    // validasi dulu; hanya tampilkan loader setelah semua lolos
    if (!validate()) {
      dispatch(actions.setStatus({ status: "idle" }));
      return;
    }
    dispatch(actions.setStatus({ status: "submitting" }));
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 30000);
    try {
      const body = new FormData();
      Object.entries(values).forEach(([name, value]) =>
        body.append(name, value),
      );
      body.append("consent", "1");
      body.append("website", hpEl ? hpEl.value : "");
      const response = await fetch(config.action, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-TOKEN": config.csrfToken,
        },
        body,
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || payload?.status !== "true") {
        throw new Error(
          payload?.message || labels.failed || "Unable to send your request.",
        );
      }
      dispatch(actions.resetForm());
      window.localStorage.removeItem(STORAGE_KEY);
      document.dispatchEvent(
        new CustomEvent("squra:form-saved", {
          detail: { root: "#consult-form-root" },
        }),
      );
      setSuccessOpen(true);
    } catch (error) {
      dispatch(
        actions.setStatus({
          status: "error",
          message:
            error.name === "AbortError"
              ? labels.timeout || "Connection timed out. Please try again."
              : error.message,
        }),
      );
    } finally {
      window.clearTimeout(timer);
    }
  };

  const field = (name, label, type = "text") => (
    <label className="sq-consult-form__field" htmlFor={`react-${name}`}>
      <span>{label} *</span>
      <input
        id={`react-${name}`}
        name={name}
        type={type}
        value={values[name]}
        onChange={update}
        disabled={busy}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `error-${name}` : undefined}
      />
      <FieldError id={`error-${name}`} message={errors[name]} />
    </label>
  );

  return (
    <>
      <form
        className="sq-consult-form"
        onSubmit={submit}
        noValidate
        data-no-draft
      >
        <div className="sq-consult-form__grid">
          {field("cfullname", labels.fullname)}
          {field("cjobPosition", labels.job)}
          {field("ccompanyName", labels.company)}
          <label
            className="sq-consult-form__field"
            htmlFor="react-ctypeIndustrie"
          >
            <span>{labels.industry} *</span>
            <select
              id="react-ctypeIndustrie"
              name="ctypeIndustrie"
              value={values.ctypeIndustrie}
              onChange={update}
              disabled={busy}
            >
              {(config.industries || []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              <option value="OTHERS">{labels.other}</option>
            </select>
            <FieldError
              id="error-ctypeIndustrie"
              message={errors.ctypeIndustrie}
            />
          </label>
        </div>
        {isOther && (
          <label
            className="sq-consult-form__field"
            htmlFor="react-specificTypeIndustries"
          >
            <span>{labels.others} *</span>
            <textarea
              id="react-specificTypeIndustries"
              name="specificTypeIndustries"
              rows="3"
              value={values.specificTypeIndustries}
              onChange={update}
              disabled={busy}
            />
            <FieldError
              id="error-specificTypeIndustries"
              message={errors.specificTypeIndustries}
            />
          </label>
        )}
        <div className="sq-consult-form__grid">
          {field("cemail", labels.email, "email")}
          {field("ccontactNumber", labels.contact, "tel")}
        </div>
        <label
          className="sq-consult-form__field"
          htmlFor="react-typeSecurityNeeds"
        >
          <span>{labels.need} *</span>
          <select
            id="react-typeSecurityNeeds"
            name="typeSecurityNeeds"
            value={values.typeSecurityNeeds}
            onChange={update}
            disabled={busy}
          >
            {(config.solutions || []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            <option value="OTHERS">{labels.other}</option>
          </select>
          <FieldError
            id="error-typeSecurityNeeds"
            message={errors.typeSecurityNeeds}
          />
        </label>
        <label className="sq-consult-form__field" htmlFor="react-specificNotes">
          <span>
            {labels.note} <small>({labels.optional})</small>
          </span>
          <textarea
            id="react-specificNotes"
            name="specificNotes"
            rows="5"
            value={values.specificNotes}
            onChange={update}
            disabled={busy}
          />
        </label>
        {message && (
          <div className="sq-consult-form__notice" role="alert">
            {message}
          </div>
        )}
        <label
          className="sq-consult-form__field"
          htmlFor="react-consent"
          style={{
            gridColumn: "1 / -1",
            display: "flex",
            flexDirection: "row",
            gap: 8,
            alignItems: "flex-start",
          }}
        >
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px" }}
          />
          <input
            id="react-consent"
            name="consent"
            type="checkbox"
            disabled={busy}
            style={{ width: 18, height: 18, flexShrink: 0, marginTop: 2 }}
          />
          <span>
            {labels.consent || (
              <>
                Saya menyetujui data pribadi yang saya kirimkan disimpan dan
                diproses oleh SQURA untuk keperluan layanan sesuai{" "}
                <a
                  href="https://www.lintasarta.net/privacy-notice/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Notice
                </a>
                .
              </>
            )}
          </span>
        </label>
        <div className="sq-consult-form__actions">
          <button type="submit" disabled={busy}>
            {busy ? labels.sending : labels.send}
          </button>
          <p>{labels.star}</p>
        </div>
      </form>
      {successOpen &&
        createPortal(
          <dialog
            open
            className="sq-consult-success"
            aria-modal="true"
            aria-labelledby="consult-success-title"
          >
            <div className="sq-consult-success__card">
              <img
                src="/media/static/templates/fe/images/icon-success.png"
                alt=""
              />
              <h2 id="consult-success-title">{labels.successTitle}</h2>
              <p>{labels.successDescription}</p>
              <button type="button" onClick={() => setSuccessOpen(false)}>
                {labels.continue}
              </button>
            </div>
          </dialog>,
          document.body,
        )}
    </>
  );
}

export function ConsultFormMount() {
  const [mount, setMount] = useState(null);
  useEffect(() => {
    const find = () => setMount(document.getElementById("consult-form-root"));
    find();
    const observer = new MutationObserver(find);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  if (!mount) return null;
  const configNode = document.getElementById("consult-form-config");
  if (!configNode) return null;
  try {
    return createPortal(
      <ConsultForm config={JSON.parse(configNode.textContent)} />,
      mount,
    );
  } catch {
    return null;
  }
}
